import { Link, Outlet, useNavigate, useSearch } from "@tanstack/react-router";
import { MailCheck } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { EnvBadge, Logo } from "../components/env-badge";
import { LegalLinks } from "../components/legal-links";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/input";
import { queryClient } from "../lib/api";
import { authClient, followAuthRedirect } from "../lib/auth-client";
import { useSystemInfo } from "../lib/system";

/** True while an OAuth client (ChatGPT, Claude …) waits for this login. */
export const isOAuthFlow = () => new URLSearchParams(window.location.search).has("sig");

export function AuthLayout() {
  return (
    <div className="flex min-h-full bg-canvas">
      <BrandPanel />
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center px-5 py-12">
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <Logo size="lg" />
          <EnvBadge />
        </div>
        <div className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-7 shadow-sm">
          <Outlet />
        </div>
        <LegalLinks className="mt-8" />
      </div>
    </div>
  );
}

/** Dark brand surface next to the sign-in forms on wide screens. */
export function BrandPanel() {
  return (
    <aside className="relative m-4 hidden w-[44%] max-w-2xl shrink-0 flex-col justify-between overflow-hidden rounded-3xl bg-ink p-12 text-white lg:flex">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_0%_100%,rgba(146,86,245,0.35),transparent),radial-gradient(50%_40%_at_100%_0%,rgba(36,87,235,0.3),transparent)]"
      />
      <div className="relative flex items-center gap-3">
        <Logo size="lg" tone="light" />
        <EnvBadge />
      </div>
      <div className="relative">
        <h2 className="text-[40px] leading-[44px] font-bold tracking-tight">
          Raum für
          <br />
          deine Ideen.
        </h2>
        <p className="mt-4 max-w-sm text-base text-[#bcb4ce]">
          Designs, Druckdaten und Reels an einem Ort. Kreativ im Ausdruck, klar im Werkzeug.
        </p>
        <div aria-hidden="true" className="brand-stripe mt-10 h-2 w-48 rounded-full" />
      </div>
    </aside>
  );
}

function AuthTitle({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold tracking-tight text-ink">{title}</h1>
      {subtitle ? <p className="mt-1 text-sm text-zinc-500">{subtitle}</p> : null}
    </div>
  );
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{message}</p>;
}

function OAuthHint() {
  if (!isOAuthFlow()) return null;
  return (
    <p className="mb-5 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-700">
      Eine externe App (z. B. ChatGPT oder Claude) möchte sich mit deinem Konto verbinden. Melde
      dich an, um fortzufahren.
    </p>
  );
}

async function afterSignIn(
  data: unknown,
  redirectTo: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
) {
  if (followAuthRedirect(data)) return;
  await queryClient.invalidateQueries({ queryKey: ["session"] });
  if (redirectTo?.startsWith("/")) window.location.href = redirectTo;
  else await navigate({ to: "/" });
}

function GoogleButton() {
  const { data } = useSystemInfo();
  if (!data?.authProviders.includes("google")) return null;
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        className="w-full"
        onClick={() => authClient.signIn.social({ provider: "google", callbackURL: "/" })}
      >
        Mit Google anmelden
      </Button>
      <div className="my-5 flex items-center gap-3 text-xs text-zinc-400">
        <div className="h-px flex-1 bg-zinc-200" /> oder <div className="h-px flex-1 bg-zinc-200" />
      </div>
    </>
  );
}

/** Second step after the password when the account uses an authenticator app. */
function TwoFactorStep({
  onDone,
  onCancel,
}: {
  onDone: (data: unknown) => Promise<void>;
  onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const clean = code.replace(/\s+/g, "");
    const { data, error: verifyError } = backup
      ? await authClient.twoFactor.verifyBackupCode({ code: clean, trustDevice })
      : await authClient.twoFactor.verifyTotp({ code: clean, trustDevice });
    setBusy(false);
    if (verifyError) {
      setError(
        verifyError.status === 429 || verifyError.code === "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE"
          ? "Zu viele Versuche. Bitte melde dich erneut an."
          : "Der Code stimmt nicht. Bitte versuche es noch einmal.",
      );
      return;
    }
    await onDone(data);
  }

  return (
    <>
      <AuthTitle
        title="Bestätigung"
        subtitle={
          backup
            ? "Gib einen deiner Wiederherstellungscodes ein."
            : "Gib den 6-stelligen Code aus deiner Authenticator-App ein."
        }
      />
      <form onSubmit={submit} className="space-y-4">
        <Field label={backup ? "Wiederherstellungscode" : "Code"}>
          <Input
            autoFocus
            required
            inputMode={backup ? "text" : "numeric"}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={backup ? "xxxxx-xxxxx" : "123456"}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-zinc-600">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
          />
          Diesem Gerät 30 Tage vertrauen
        </label>
        <FormError message={error} />
        <Button type="submit" className="w-full" loading={busy}>
          Bestätigen
        </Button>
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <button type="button" className="text-zinc-500 hover:text-zinc-900" onClick={onCancel}>
          Zurück
        </button>
        <button
          type="button"
          className="font-medium text-brand-600 hover:text-brand-700"
          onClick={() => {
            setBackup(!backup);
            setCode("");
            setError(null);
          }}
        >
          {backup ? "Code aus der App verwenden" : "Wiederherstellungscode verwenden"}
        </button>
      </div>
    </>
  );
}

export function SignInPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const { data: system } = useSystemInfo();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: signInError } = await authClient.signIn.email({
      email,
      password,
      rememberMe: true,
    });
    setBusy(false);
    if (signInError) {
      const banned = signInError.code === "BANNED_USER";
      setUnverified(signInError.status === 403 && !banned);
      setError(
        banned
          ? (signInError.message ?? "Dein Konto ist gesperrt.")
          : signInError.status === 403
            ? "Bitte bestätige zuerst deine E-Mail-Adresse."
            : signInError.status === 401
              ? "E-Mail oder Passwort ist falsch."
              : (signInError.message ?? "Anmeldung fehlgeschlagen"),
      );
      return;
    }
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      setTwoFactor(true);
      return;
    }
    await afterSignIn(data, search.redirect, navigate);
  }

  if (twoFactor) {
    return (
      <TwoFactorStep
        onDone={(data) => afterSignIn(data, search.redirect, navigate)}
        onCancel={() => {
          setTwoFactor(false);
          setPassword("");
        }}
      />
    );
  }

  return (
    <>
      <AuthTitle title="Anmelden" subtitle="Willkommen zurück!" />
      <OAuthHint />
      <GoogleButton />
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-Mail">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Passwort">
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <FormError message={error} />
        {unverified ? (
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            onClick={async () => {
              await authClient.sendVerificationEmail({ email, callbackURL: "/" });
              setError("Wir haben dir eine neue Bestätigungs-E-Mail geschickt.");
            }}
          >
            Bestätigungs-E-Mail erneut senden
          </Button>
        ) : null}
        <Button type="submit" className="w-full" loading={busy}>
          Anmelden
        </Button>
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-zinc-500 hover:text-zinc-900">
          Passwort vergessen?
        </Link>
        {system?.signupEnabled !== false ? (
          <a
            href={`/sign-up${window.location.search}`}
            className="font-medium text-brand-600 hover:text-brand-700"
          >
            Konto erstellen
          </a>
        ) : null}
      </div>
    </>
  );
}

export function SignUpPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const { data: system } = useSystemInfo();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: signUpError } = await authClient.signUp.email({
      name,
      email,
      password,
      callbackURL: search.redirect ?? "/",
    });
    setBusy(false);
    if (signUpError) {
      setError(
        signUpError.status === 422
          ? "Für diese E-Mail-Adresse gibt es bereits ein Konto."
          : (signUpError.message ?? "Registrierung fehlgeschlagen"),
      );
      return;
    }
    if (system?.emailVerificationRequired) {
      setSent(true);
      return;
    }
    await afterSignIn(data, search.redirect, navigate);
  }

  if (system?.signupEnabled === false) {
    return (
      <>
        <AuthTitle
          title="Registrierung geschlossen"
          subtitle="Neue Konten werden derzeit nur per Einladung vergeben."
        />
        <Link to="/sign-in" className="text-sm font-medium text-brand-600">
          Zur Anmeldung
        </Link>
      </>
    );
  }

  if (sent) {
    return (
      <div className="text-center">
        <MailCheck className="mx-auto mb-3 size-10 text-brand-600" />
        <AuthTitle
          title="Fast geschafft!"
          subtitle={`Wir haben eine E-Mail an ${email} geschickt. Bitte bestätige deine Adresse über den Link darin.`}
        />
      </div>
    );
  }

  return (
    <>
      <AuthTitle title="Konto erstellen" subtitle="Kostenlos starten, in wenigen Sekunden." />
      <OAuthHint />
      <GoogleButton />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <Input
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="E-Mail">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Passwort" hint="Mindestens 10 Zeichen">
          <Input
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <FormError message={error} />
        <p className="text-xs text-zinc-500">
          Mit der Registrierung stimmst du den{" "}
          <a href="/nutzungsbedingungen" target="_blank" rel="noreferrer" className="underline">
            Nutzungsbedingungen
          </a>{" "}
          zu. Wie wir mit deinen Daten umgehen, steht in der{" "}
          <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline">
            Datenschutzerklärung
          </a>
          .
        </p>
        <Button type="submit" className="w-full" loading={busy}>
          Konto erstellen
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-zinc-500">
        Schon registriert?{" "}
        <a
          href={`/sign-in${window.location.search}`}
          className="font-medium text-brand-600 hover:text-brand-700"
        >
          Anmelden
        </a>
      </p>
    </>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    await authClient.requestPasswordReset({ email, redirectTo: "/reset-password" });
    setBusy(false);
    setSent(true);
  }

  return (
    <>
      <AuthTitle
        title="Passwort vergessen"
        subtitle="Wir schicken dir einen Link, mit dem du ein neues Passwort festlegen kannst."
      />
      {sent ? (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Falls ein Konto mit dieser Adresse existiert, ist die E-Mail unterwegs.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="E-Mail">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Button type="submit" className="w-full" loading={busy}>
            Link anfordern
          </Button>
        </form>
      )}
      <Link
        to="/sign-in"
        className="mt-6 block text-center text-sm text-zinc-500 hover:text-zinc-900"
      >
        Zurück zur Anmeldung
      </Link>
    </>
  );
}

export function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string; error?: string };
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(
    search.error ? "Der Link ist ungültig oder abgelaufen." : null,
  );
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!search.token) return;
    setBusy(true);
    const { error: resetError } = await authClient.resetPassword({
      newPassword: password,
      token: search.token,
    });
    setBusy(false);
    if (resetError) {
      setError(resetError.message ?? "Der Link ist ungültig oder abgelaufen.");
      return;
    }
    await navigate({ to: "/sign-in" });
  }

  return (
    <>
      <AuthTitle title="Neues Passwort festlegen" />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Neues Passwort" hint="Mindestens 10 Zeichen">
          <Input
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <FormError message={error} />
        <Button type="submit" className="w-full" loading={busy} disabled={!search.token}>
          Passwort speichern
        </Button>
      </form>
    </>
  );
}
